import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { adminAPI, metricsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const UsersEditor = () => {
  const { user: currentUser } = useAuth();
  // Flat three-tab layout: 'overview' | 'users' | 'activity'
  const [activeTab, setActiveTab] = useState('overview');

  // ── Users state ──
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [usersSortKey, setUsersSortKey] = useState('created_at');
  const [usersSortDir, setUsersSortDir] = useState('desc');
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerForm, setRegisterForm] = useState({ email: '', fullName: '', password: '', confirmPassword: '' });
  const [registerErrors, setRegisterErrors] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);

  // ── Metrics state (Overview tab) ──
  const [summary, setSummary] = useState(null);
  const [loginTrend, setLoginTrend] = useState(null);
  const [appUsage, setAppUsage] = useState(null);
  const [activityLog, setActivityLog] = useState({ total: 0, logs: [] });
  const [byDomain, setByDomain] = useState(null);
  const [activeUsersData, setActiveUsersData] = useState(null);
  const [newVsReturning, setNewVsReturning] = useState(null);
  const [dormant, setDormant] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [powerUsers, setPowerUsers] = useState(null);
  const [showDormantList, setShowDormantList] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterDays, setFilterDays] = useState(7);

  // ── Client Activity state ──
  const [usersActivity, setUsersActivity] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userFilterDays, setUserFilterDays] = useState(30);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityDomain, setActivityDomain] = useState('all');
  const [activityStatus, setActivityStatus] = useState('all');
  const [sortKey, setSortKey] = useState('last_login');
  const [sortDir, setSortDir] = useState('desc');

  // ── Fetch users on mount ──
  useEffect(() => {
    fetchUsers();
  }, []);

  // ── Fetch overview metrics (client-only) when Overview tab is opened ──
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  useEffect(() => {
    if (activeTab === 'overview' && !overviewLoaded) {
      fetchAllMetrics();
      setOverviewLoaded(true);
    }
  }, [activeTab]);

  // Recent-activity table re-fetches when its filters change, while the
  // Overview tab is visible.
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchActivityLog();
    }
  }, [filterType, filterDays, activeTab]);

  // Client Activity tab fetches its user list on open + when date range changes.
  useEffect(() => {
    if (activeTab === 'activity') {
      fetchUsersActivity();
    }
  }, [userFilterDays, activeTab]);

  // ── Users API ──
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}/toggle-active`);
      setMessage({ type: 'success', text: response.data.message });
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update user' });
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      setMessage({ type: 'success', text: 'User deleted successfully' });
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete user' });
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(password)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('At least one lowercase letter');
    if (!/\d/.test(password)) errors.push('At least one digit');
    if (!/[^A-Za-z0-9]/.test(password)) errors.push('At least one special character');
    return errors;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!registerForm.email) errors.email = 'Email is required';
    if (!registerForm.fullName) errors.fullName = 'Full name is required';
    const pwErrors = validatePassword(registerForm.password);
    if (pwErrors.length > 0) errors.password = pwErrors.join(', ');
    if (registerForm.password !== registerForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setRegisterErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsRegistering(true);
    try {
      const result = await adminAPI.registerUser(registerForm.email, registerForm.password, registerForm.fullName);
      setMessage({ type: 'success', text: result.message });
      setRegisterForm({ email: '', fullName: '', password: '', confirmPassword: '' });
      setRegisterErrors({});
      setShowRegisterForm(false);
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to register user' });
    } finally {
      setIsRegistering(false);
    }
  };

  // ── Metrics API ──
  // Overview tab: all metrics fetched with exclude_internal=true so client figures
  // reflect real product-market engagement, not Drummond staff noise.
  const fetchAllMetrics = async () => {
    setMetricsLoading(true);
    try {
      const [
        summaryData,
        trendData,
        usageData,
        byDomainData,
        activeUsers,
        nvr,
        dormantData,
        heatmapData,
        powerUsersData,
      ] = await Promise.all([
        metricsAPI.getSummary(true),
        metricsAPI.getLoginTrend(30, true),
        metricsAPI.getAppUsage(30, true),
        metricsAPI.getByDomain(30),
        metricsAPI.getActiveUsers(30),
        metricsAPI.getNewVsReturning(12),
        metricsAPI.getDormantUsers(),
        metricsAPI.getActivityHeatmap(30),
        metricsAPI.getPowerUsers(30, 10),
      ]);
      setSummary(summaryData);
      setLoginTrend(trendData);
      setAppUsage(usageData);
      setByDomain(byDomainData);
      setActiveUsersData(activeUsers);
      setNewVsReturning(nvr);
      setDormant(dormantData);
      setHeatmap(heatmapData);
      setPowerUsers(powerUsersData);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setMessage({ type: 'error', text: 'Failed to load metrics data' });
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const params = { days: filterDays, limit: 100, exclude_internal: true };
      if (filterType) params.activity_type = filterType;
      const data = await metricsAPI.getActivityLog(params);
      setActivityLog(data);
    } catch (err) {
      console.error('Error fetching activity log:', err);
    }
  };

  const fetchUsersActivity = async () => {
    try {
      const data = await metricsAPI.getUsersActivity(userFilterDays);
      setUsersActivity(data);
    } catch (err) {
      console.error('Error fetching users activity:', err);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const data = await metricsAPI.getUserActivity(userId, userFilterDays);
      setUserDetails(data);
      setSelectedUser(userId);
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Delete activity logs older than 12 months?')) return;
    try {
      const result = await metricsAPI.cleanupOldLogs();
      setMessage({ type: 'success', text: result.message });
      fetchAllMetrics();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to cleanup logs' });
    }
  };

  const handleExportLogs = async () => {
    try {
      // Overview-originated export excludes internal Drummond staff.
      const response = await metricsAPI.exportLogs(true);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setMessage({ type: 'error', text: 'Failed to export logs' });
    }
  };

  const handleViewActivity = (userId) => {
    setActiveTab('activity');
    fetchUsersActivity().then(() => {
      fetchUserDetails(userId);
    });
  };

  // ── Helpers ──
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Sydney',
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'login': return '🔑';
      case 'app_access': return '📱';
      case 'page_view': return '📄';
      default: return '•';
    }
  };

  // ── Users derived data ──
  const domains = [...new Set(users.map(u => u.domain))].sort();
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDomain = filterDomain === 'all' || user.domain === filterDomain;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active);
    return matchesSearch && matchesDomain && matchesStatus;
  });
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const dir = usersSortDir === 'asc' ? 1 : -1;
    const av = a[usersSortKey];
    const bv = b[usersSortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (usersSortKey === 'is_active') return (Number(av) - Number(bv)) * dir;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });

  // ── Metrics chart data ──
  const loginChartData = loginTrend ? {
    labels: loginTrend.dates.map(d => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })),
    datasets: [{
      label: 'Logins',
      data: loginTrend.counts,
      borderColor: '#072040',
      backgroundColor: 'rgba(0, 51, 102, 0.1)',
      fill: true,
      tension: 0.3,
    }],
  } : null;

  const appChartData = appUsage ? {
    labels: appUsage.apps,
    datasets: [{
      label: 'App Access Count',
      data: appUsage.counts,
      backgroundColor: ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#6c757d'],
    }],
  } : null;

  const byDomainChartData = byDomain ? {
    labels: byDomain.map(d => d.company_name || d.domain),
    datasets: [{
      label: 'Logins',
      data: byDomain.map(d => d.logins),
      backgroundColor: '#072040',
    }],
  } : null;

  const newVsReturningData = newVsReturning ? {
    labels: newVsReturning.weeks.map(w => new Date(w).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })),
    datasets: [
      {
        label: 'New',
        data: newVsReturning.new,
        borderColor: '#f0a500',
        backgroundColor: 'rgba(240, 165, 0, 0.2)',
        fill: true,
        tension: 0.3,
      },
      {
        label: 'Returning',
        data: newVsReturning.returning,
        borderColor: '#072040',
        backgroundColor: 'rgba(7, 32, 64, 0.2)',
        fill: true,
        tension: 0.3,
      },
    ],
  } : null;

  const dauSparklineData = activeUsersData ? {
    labels: activeUsersData.dau_series.map(p => p.date),
    datasets: [{
      label: 'DAU',
      data: activeUsersData.dau_series.map(p => p.count),
      borderColor: '#599BE3',
      backgroundColor: 'rgba(89, 155, 227, 0.15)',
      fill: true,
      tension: 0.3,
      pointRadius: 0,
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
  };

  const lineChartWithLegend = {
    ...chartOptions,
    plugins: { legend: { display: true, position: 'bottom' } },
  };

  const sparklineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: { x: { display: false }, y: { display: false } },
  };

  // ── Client Activity derived data ──
  const activityDomains = [...new Set(usersActivity.map(u => u.domain))].filter(Boolean).sort();
  const daysSince = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };
  const filteredActivity = usersActivity.filter(u => {
    const term = activitySearch.toLowerCase();
    const matchesSearch = !term ||
      (u.email && u.email.toLowerCase().includes(term)) ||
      (u.full_name && u.full_name.toLowerCase().includes(term));
    const matchesDomain = activityDomain === 'all' || u.domain === activityDomain;
    // Status uses last_activity_at (any activity), falls back to last_login if older rows lack it.
    const lastAny = u.last_activity_at || u.last_login;
    const ds = daysSince(lastAny);
    let matchesStatus = true;
    switch (activityStatus) {
      case 'active_7d':   matchesStatus = ds !== null && ds <= 7; break;
      case 'active_30d':  matchesStatus = ds !== null && ds <= 30; break;
      case 'dormant_30d': matchesStatus = ds !== null && ds > 30; break;
      case 'dormant_90d': matchesStatus = ds !== null && ds > 90; break;
      case 'never_logged_in': matchesStatus = lastAny == null; break;
      case 'all':
      default: matchesStatus = true;
    }
    return matchesSearch && matchesDomain && matchesStatus;
  });

  const sortedActivity = [...filteredActivity].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const av = a[sortKey];
    const bv = b[sortKey];
    // Null handling — nulls last regardless of direction
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'full_name' || key === 'domain' || key === 'most_used_app' ? 'asc' : 'desc');
    }
  };

  const SortableTh = ({ label, sortField, className = '' }) => {
    const active = sortKey === sortField;
    const icon = !active ? null : (sortDir === 'asc'
      ? <i className="bi bi-caret-up-fill ms-1" />
      : <i className="bi bi-caret-down-fill ms-1" />);
    return (
      <th className={className} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(sortField)}>
        {label}{icon}
      </th>
    );
  };

  const toggleUsersSort = (key) => {
    if (usersSortKey === key) {
      setUsersSortDir(usersSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setUsersSortKey(key);
      setUsersSortDir(key === 'email' || key === 'full_name' || key === 'domain' ? 'asc' : 'desc');
    }
  };

  const UsersSortableTh = ({ label, sortField, className = '' }) => {
    const active = usersSortKey === sortField;
    const icon = !active ? null : (usersSortDir === 'asc'
      ? <i className="bi bi-caret-up-fill ms-1" />
      : <i className="bi bi-caret-down-fill ms-1" />);
    return (
      <th className={className} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleUsersSort(sortField)}>
        {label}{icon}
      </th>
    );
  };

  // Peak heatmap value for colour scaling
  const heatmapMax = heatmap ? Math.max(1, ...heatmap.grid.flat()) : 1;
  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div>
      <h4 className="mb-1">Users & Activity</h4>
      <p className="text-muted mb-3">Manage user accounts and track portal activity.</p>

      {/* Flat three-tab layout */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
            Users
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>
            Client Activity
          </button>
        </li>
      </ul>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* ════════════════ USERS TAB ════════════════ */}
      {activeTab === 'users' && (
        <>
          {/* Register User Button */}
          <div className="mb-3">
            <button className="btn btn-primary" onClick={() => setShowRegisterForm(!showRegisterForm)}>
              {showRegisterForm ? 'Cancel' : 'Register User'}
            </button>
          </div>

          {/* Registration Form */}
          {showRegisterForm && (
            <div className="card mb-4">
              <div className="card-header"><strong>Register New User</strong></div>
              <div className="card-body">
                <form onSubmit={handleRegisterSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className={`form-control ${registerErrors.email ? 'is-invalid' : ''}`}
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        placeholder="user@domain.com"
                      />
                      {registerErrors.email && <div className="invalid-feedback">{registerErrors.email}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className={`form-control ${registerErrors.fullName ? 'is-invalid' : ''}`}
                        value={registerForm.fullName}
                        onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
                        placeholder="John Smith"
                      />
                      {registerErrors.fullName && <div className="invalid-feedback">{registerErrors.fullName}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className={`form-control ${registerErrors.password ? 'is-invalid' : ''}`}
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      />
                      {registerErrors.password && <div className="invalid-feedback">{registerErrors.password}</div>}
                      <div className="form-text">Min 12 characters, with uppercase, lowercase, digit and special character.</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className={`form-control ${registerErrors.confirmPassword ? 'is-invalid' : ''}`}
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      />
                      {registerErrors.confirmPassword && <div className="invalid-feedback">{registerErrors.confirmPassword}</div>}
                    </div>
                  </div>
                  <div className="mt-3">
                    <button type="submit" className="btn btn-success me-2" disabled={isRegistering}>
                      {isRegistering ? (<><span className="spinner-border spinner-border-sm me-1"></span>Registering...</>) : 'Register'}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => {
                      setShowRegisterForm(false);
                      setRegisterForm({ email: '', fullName: '', password: '', confirmPassword: '' });
                      setRegisterErrors({});
                    }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-5">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select className="form-select" value={filterDomain} onChange={(e) => setFilterDomain(e.target.value)}>
                    <option value="all">All Domains</option>
                    {domains.map(domain => (
                      <option key={domain} value={domain}>{domain}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <span className="badge bg-secondary fs-6 w-100 py-2">
                    {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="card-body p-0">
              {isLoading ? (
                <div className="text-center py-4"><div className="spinner-border spinner-border-sm"></div></div>
              ) : sortedUsers.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0">No users found</p>
              ) : (
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <UsersSortableTh label="Email" sortField="email" />
                      <UsersSortableTh label="Name" sortField="full_name" />
                      <UsersSortableTh label="Domain" sortField="domain" />
                      <UsersSortableTh label="Status" sortField="is_active" />
                      <UsersSortableTh label="Created" sortField="created_at" />
                      <th style={{ width: '280px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map(user => (
                      <tr key={user.id} className={!user.is_active ? 'table-secondary' : ''}>
                        <td>
                          {user.email}
                          {user.id === currentUser?.id && <span className="badge bg-info ms-2">You</span>}
                        </td>
                        <td>{user.full_name || <span className="text-muted">-</span>}</td>
                        <td>
                          <span className={`badge ${user.domain === 'drummondcp.com' ? 'bg-primary' : 'bg-secondary'}`}>
                            {user.domain}
                          </span>
                        </td>
                        <td>
                          {user.is_active
                            ? <span className="badge bg-success">Active</span>
                            : <span className="badge bg-danger">Inactive</span>}
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          {user.id === currentUser?.id ? (
                            <span className="text-muted small">Cannot modify self</span>
                          ) : (
                            <div className="d-flex gap-1 flex-wrap">
                              <button
                                className={`btn btn-sm ${user.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                onClick={() => handleToggleActive(user.id)}
                              >
                                {user.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteUser(user.id, user.email)}
                              >
                                Delete
                              </button>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleViewActivity(user.id)}
                                title="View client activity"
                              >
                                Activity
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="row mt-4">
            <div className="col-md-4">
              <div className="card bg-light">
                <div className="card-body text-center">
                  <h3 className="mb-0">{users.length}</h3>
                  <small className="text-muted">Total Users</small>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card bg-light">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-success">{users.filter(u => u.is_active).length}</h3>
                  <small className="text-muted">Active Users</small>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card bg-light">
                <div className="card-body text-center">
                  <h3 className="mb-0">{domains.length}</h3>
                  <small className="text-muted">Domains</small>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ════════════════ OVERVIEW TAB ════════════════ */}
      {activeTab === 'overview' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="text-muted">Metrics below exclude Drummond internal staff and reflect client usage only.</small>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary btn-sm" onClick={handleExportLogs}>
                Export CSV
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={handleCleanup}>
                Cleanup Old Logs
              </button>
            </div>
          </div>

          {metricsLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Primary KPI row */}
              {summary && (
                <div className="row mb-4">
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0" style={{ color: '#072040' }}>{summary.logins_today}</h2>
                        <small className="text-muted">Logins Today</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0" style={{ color: '#072040' }}>{summary.logins_week}</h2>
                        <small className="text-muted">Logins (7 days)</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0" style={{ color: '#f0a500' }}>{summary.unique_users_week}</h2>
                        <small className="text-muted">Active Users (7 days)</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0">{summary.total_users}</h2>
                        <small className="text-muted">Total Client Users</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* DAU / WAU / MAU + stickiness */}
              {activeUsersData && (
                <div className="row mb-4">
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0" style={{ color: '#599BE3' }}>{activeUsersData.dau}</h2>
                        <small className="text-muted d-block">DAU</small>
                        <small className="text-muted">Stickiness: {(activeUsersData.stickiness * 100).toFixed(1)}%</small>
                        <div style={{ height: '40px' }} className="mt-2">
                          {dauSparklineData && <Line data={dauSparklineData} options={sparklineOptions} />}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0" style={{ color: '#072040' }}>{activeUsersData.wau}</h2>
                        <small className="text-muted">WAU (7-day)</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <h2 className="mb-0" style={{ color: '#072040' }}>{activeUsersData.mau}</h2>
                        <small className="text-muted">MAU (30-day)</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card h-100 border-warning">
                      <div className="card-body text-center" style={{ cursor: 'pointer' }} onClick={() => setShowDormantList(!showDormantList)}>
                        <h2 className="mb-0" style={{ color: '#6c757d' }}>{dormant?.buckets?.d30 ?? '—'}</h2>
                        <small className="text-muted">Dormant 30+ days</small>
                        {dormant && (
                          <div className="mt-1">
                            <small className="text-muted">
                              60d: {dormant.buckets.d60} · 90d: {dormant.buckets.d90}
                            </small>
                          </div>
                        )}
                        <small className="d-block text-primary mt-1">
                          {showDormantList ? 'Hide list ▲' : 'Show list ▼'}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dormant users accordion */}
              {showDormantList && dormant && (
                <div className="card mb-4">
                  <div className="card-header"><h6 className="mb-0">Dormant users (30+ days)</h6></div>
                  <div className="card-body p-0">
                    {dormant.users.length === 0 ? (
                      <p className="text-muted text-center py-4 mb-0">No data for this period</p>
                    ) : (
                      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>User</th>
                              <th>Domain</th>
                              <th>Last Login</th>
                              <th className="text-center">Days Since</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {dormant.users.map(u => (
                              <tr key={u.id}>
                                <td>
                                  <span className="fw-semibold">{u.full_name || u.email}</span><br />
                                  <small className="text-muted">{u.email}</small>
                                </td>
                                <td><span className="badge bg-light text-dark">{u.domain}</span></td>
                                <td><small className="text-muted">{u.last_login ? formatDateTime(u.last_login) : 'Never'}</small></td>
                                <td className="text-center">{u.days_since ?? '∞'}</td>
                                <td>
                                  <button className="btn btn-sm btn-outline-warning" onClick={() => handleToggleActive(u.id)}>
                                    Deactivate
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Login trend + App usage (existing charts) */}
              <div className="row mb-4">
                <div className="col-md-8 mb-3">
                  <div className="card h-100">
                    <div className="card-header"><h6 className="mb-0">Login Trend (30 days)</h6></div>
                    <div className="card-body" style={{ height: '250px' }}>
                      {loginChartData && <Line data={loginChartData} options={chartOptions} />}
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="card h-100">
                    <div className="card-header"><h6 className="mb-0">App Usage (30 days)</h6></div>
                    <div className="card-body" style={{ height: '250px' }}>
                      {appChartData && appChartData.labels.length > 0 ? (
                        <Bar data={appChartData} options={chartOptions} />
                      ) : (
                        <div className="text-center text-muted py-5">No app usage data yet</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* New vs Returning + Per-brand breakdown */}
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <div className="card h-100">
                    <div className="card-header"><h6 className="mb-0">New vs Returning (12 weeks)</h6></div>
                    <div className="card-body" style={{ height: '260px' }}>
                      {newVsReturningData && <Line data={newVsReturningData} options={lineChartWithLegend} />}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="card h-100">
                    <div className="card-header"><h6 className="mb-0">Per-Brand Usage (30 days)</h6></div>
                    <div className="card-body" style={{ height: '260px' }}>
                      {byDomain && byDomain.length > 0 ? (
                        <Bar
                          data={byDomainChartData}
                          options={{ ...chartOptions, indexAxis: 'y' }}
                        />
                      ) : (
                        <div className="text-center text-muted py-5">No data for this period</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Recent Activity</h6>
                  <div className="d-flex gap-2">
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                      <option value="">All Types</option>
                      <option value="login">Logins</option>
                      <option value="app_access">App Access</option>
                      <option value="page_view">Page Views</option>
                    </select>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={filterDays} onChange={(e) => setFilterDays(Number(e.target.value))}>
                      <option value={1}>Today</option>
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                  </div>
                </div>
                <div className="card-body p-0">
                  {activityLog.logs.length === 0 ? (
                    <p className="text-muted text-center py-4 mb-0">No activity recorded yet</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>User</th>
                            <th>Type</th>
                            <th>Resource</th>
                            <th>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityLog.logs.map((log) => (
                            <tr key={log.id}>
                              <td className="text-center">{getActivityIcon(log.activity_type)}</td>
                              <td><span className="fw-semibold">{log.user_email}</span></td>
                              <td>
                                <span className={`badge ${log.activity_type === 'login' ? 'bg-success' : log.activity_type === 'app_access' ? 'bg-primary' : 'bg-secondary'}`}>
                                  {log.activity_type}
                                </span>
                              </td>
                              <td>{log.resource || '-'}</td>
                              <td><small className="text-muted">{formatDateTime(log.created_at)}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {activityLog.total > 100 && (
                  <div className="card-footer text-muted text-center">Showing 100 of {activityLog.total} records</div>
                )}
              </div>

              {/* Per-brand table */}
              {byDomain && byDomain.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header"><h6 className="mb-0">Per-Brand Breakdown</h6></div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Brand</th>
                            <th>Domain</th>
                            <th className="text-center">Users</th>
                            <th className="text-center">Active</th>
                            <th className="text-center">Logins</th>
                            <th className="text-center">App Access</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byDomain.map(row => (
                            <tr key={row.domain}>
                              <td className="fw-semibold">{row.company_name}</td>
                              <td><small className="text-muted">{row.domain}</small></td>
                              <td className="text-center">{row.users}</td>
                              <td className="text-center">{row.active_users}</td>
                              <td className="text-center">{row.logins}</td>
                              <td className="text-center">{row.app_accesses}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity heatmap + Power users */}
              <div className="row mb-4">
                <div className="col-md-8 mb-3">
                  <div className="card h-100">
                    <div className="card-header"><h6 className="mb-0">Activity Heatmap — Day × Hour (AEST, 30 days)</h6></div>
                    <div className="card-body">
                      {heatmap ? (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="mb-0" style={{ borderCollapse: 'separate', borderSpacing: '2px', fontSize: '0.75rem' }}>
                            <thead>
                              <tr>
                                <th></th>
                                {Array.from({ length: 24 }).map((_, h) => (
                                  <th key={h} className="text-center text-muted" style={{ minWidth: '22px', fontWeight: 'normal' }}>
                                    {h % 3 === 0 ? h : ''}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {DAY_LABELS.map((day, rowIdx) => (
                                <tr key={day}>
                                  <td className="text-muted pe-2" style={{ fontWeight: 500 }}>{day}</td>
                                  {heatmap.grid[rowIdx].map((count, colIdx) => {
                                    const alpha = count === 0 ? 0.05 : 0.15 + (count / heatmapMax) * 0.85;
                                    return (
                                      <td
                                        key={colIdx}
                                        title={`${day} ${colIdx}:00 — ${count} events`}
                                        style={{
                                          width: '22px',
                                          height: '22px',
                                          backgroundColor: `rgba(7, 32, 64, ${alpha})`,
                                          border: '1px solid #E7E6E6',
                                          borderRadius: '2px',
                                        }}
                                      />
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-5">No data for this period</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="card h-100">
                    <div className="card-header"><h6 className="mb-0">Top 10 Power Users (30 days)</h6></div>
                    <div className="card-body p-0">
                      {powerUsers && powerUsers.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>User</th>
                                <th className="text-center">Logins</th>
                                <th className="text-center">Apps</th>
                              </tr>
                            </thead>
                            <tbody>
                              {powerUsers.map(u => (
                                <tr key={u.id}>
                                  <td>
                                    <div>
                                      <span className="fw-semibold small">{u.full_name || u.email}</span><br />
                                      <small className="text-muted">{u.domain}</small>
                                    </div>
                                  </td>
                                  <td className="text-center">{u.logins}</td>
                                  <td className="text-center">{u.app_accesses}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">No data for this period</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ════════════════ CLIENT ACTIVITY TAB ════════════════ */}
      {activeTab === 'activity' && (
        <div className="row">
          <div className={selectedUser ? 'col-md-6' : 'col-12'}>
            {/* Filter bar */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-2 align-items-center">
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search name or email..."
                      value={activitySearch}
                      onChange={(e) => setActivitySearch(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={activityDomain} onChange={(e) => setActivityDomain(e.target.value)}>
                      <option value="all">All brands</option>
                      {activityDomains.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={activityStatus} onChange={(e) => setActivityStatus(e.target.value)}>
                      <option value="all">Any status</option>
                      <option value="active_7d">Active (7d)</option>
                      <option value="active_30d">Active (30d)</option>
                      <option value="dormant_30d">Dormant 30+ days</option>
                      <option value="dormant_90d">Dormant 90+ days</option>
                      <option value="never_logged_in">Never logged in</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={userFilterDays} onChange={(e) => setUserFilterDays(Number(e.target.value))}>
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={60}>Last 60 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                  </div>
                  <div className="col-md-3 text-end">
                    <span className="badge bg-secondary fs-6">
                      {sortedActivity.length} client{sortedActivity.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body p-0">
                {sortedActivity.length === 0 ? (
                  <p className="text-muted text-center py-4 mb-0">No client users match these filters</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <SortableTh label="User" sortField="full_name" />
                          <SortableTh label="Domain" sortField="domain" />
                          <SortableTh label="Logins" sortField="login_count" className="text-center" />
                          <SortableTh label="Last Login" sortField="last_login" />
                          <SortableTh label="App Access" sortField="app_access_count" className="text-center" />
                          <SortableTh label="Most Used" sortField="most_used_app" />
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedActivity.map((user) => (
                          <tr key={user.id} className={selectedUser === user.id ? 'table-active' : ''} style={{ cursor: 'pointer' }} onClick={() => fetchUserDetails(user.id)}>
                            <td>
                              <div>
                                <span className="fw-semibold">{user.full_name || 'Unknown'}</span><br />
                                <small className="text-muted">{user.email}</small>
                              </div>
                            </td>
                            <td><span className="badge bg-light text-dark">{user.domain}</span></td>
                            <td className="text-center">
                              <span className={`badge ${user.login_count > 0 ? 'bg-success' : 'bg-secondary'}`}>{user.login_count}</span>
                            </td>
                            <td><small className="text-muted">{user.last_login ? formatDateTime(user.last_login) : 'Never'}</small></td>
                            <td className="text-center">{user.app_access_count}</td>
                            <td><small>{user.most_used_app || '-'}</small></td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); fetchUserDetails(user.id); }}>View</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* User Details Panel */}
          {selectedUser && userDetails && (
            <div className="col-md-6">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">{userDetails.user.full_name || userDetails.user.email}</h6>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSelectedUser(null); setUserDetails(null); }}>Close</button>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <small className="text-muted d-block">Email</small>
                    <span>{userDetails.user.email}</span>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Domain</small>
                    <span className="badge bg-light text-dark">{userDetails.user.domain}</span>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Last Login</small>
                    <span>{userDetails.last_login ? formatDateTime(userDetails.last_login) : 'Never'}</span>
                  </div>

                  <div className="row mb-3">
                    <div className="col-4 text-center">
                      <h4 className="mb-0" style={{ color: '#072040' }}>{userDetails.activity_counts.login || 0}</h4>
                      <small className="text-muted">Logins</small>
                    </div>
                    <div className="col-4 text-center">
                      <h4 className="mb-0" style={{ color: '#f0a500' }}>{userDetails.activity_counts.app_access || 0}</h4>
                      <small className="text-muted">App Access</small>
                    </div>
                    <div className="col-4 text-center">
                      <h4 className="mb-0">{userDetails.activity_counts.page_view || 0}</h4>
                      <small className="text-muted">Page Views</small>
                    </div>
                  </div>

                  <h6 className="mt-4 mb-2">Recent Activity</h6>
                  {userDetails.recent_activity.length === 0 ? (
                    <p className="text-muted small">No recent activity</p>
                  ) : (
                    <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {userDetails.recent_activity.slice(0, 20).map((activity) => (
                        <div key={activity.id} className="list-group-item px-0 py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <span className="me-2">{getActivityIcon(activity.activity_type)}</span>
                              <span className={`badge ${activity.activity_type === 'login' ? 'bg-success' : activity.activity_type === 'app_access' ? 'bg-primary' : 'bg-secondary'} me-2`}>
                                {activity.activity_type}
                              </span>
                              <small>{activity.resource || ''}</small>
                            </div>
                            <small className="text-muted">{formatDateTime(activity.created_at)}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsersEditor;
